import React, { useState, useEffect } from "react";
import CommonTable from "../../components/commonComponent/CommanTable";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { toastError } from "../../utils/alertHelper";

// Table columns for BOM items
const bomItemColumns = [
  { key: "srNo", label: "Sr No." },
  { key: "itemName", label: "Item Name" },
  { key: "variantName", label: "Variant" },
  { key: "companyName", label: "Company" },
  { key: "unit", label: "Unit" },
  { key: "quantity", label: "Quantity" },
  { key: "rate", label: "Rate(₹)" },
  { key: "total", label: "Total(₹)" }
];

export default function BomView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bomData, setBomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchBomData = async () => {
      try {
        const response = await axiosInstance.get(`/api/bom/${id}`);
        if (response.data && response.data.Status === 1 && response.data.bom) {
          setBomData(response.data.bom);
        } else {
          toastError("Failed to fetch BOM data. Please try again.");
        }
      } catch (error) {
        console.error("Error fetching BOM data:", error);
        toastError("Failed to fetch BOM data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBomData();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading BOM details...</p>
        </div>
      </div>
    );
  }

  if (!bomData) {
    return (
      <div className="container py-4">
        <div className="text-center py-4">
          <h4>BOM not found</h4>
          <button 
            className="btn btn-primary mt-2"
            onClick={() => navigate('/user/bom')}
          >
            Back to BOM List
          </button>
        </div>
      </div>
    );
  }

  // Prepare table data
  const tableData = bomData.items ? bomData.items
    .filter(item => {
      const itemName = item.item?.name || '';
      const variantName = item.variant?.name || '';
      const searchText = searchTerm.toLowerCase();
      return itemName.toLowerCase().includes(searchText) || 
             variantName.toLowerCase().includes(searchText);
    })
    .map((item, index) => {
      const quantity = item.qty || 0;
      const rate = item.variant?.rate || 0;
      const total = quantity * rate;
      
      return {
        srNo: index + 1,
        itemName: item.item?.name || '',
        variantName: item.variant?.name || '',
        companyName: item.variant?.companyName || '',
        unit: item.variant?.unit?.name || '',
        quantity: quantity,
        rate: rate.toFixed(2),
        total: total.toFixed(2)
      };
    }) : [];

  // Calculate totals
  const calculateTotals = () => {
    let subTotal = 0;
    let totalGst = 0;

    tableData.forEach(row => {
      const lineTotal = parseFloat(row.total) || 0;
      subTotal += lineTotal;
      
      // Find the corresponding variant for GST calculation
      const bomItem = bomData.items?.find((item, index) => index + 1 === row.srNo);
      if (bomItem?.variant?.gst) {
        totalGst += (lineTotal * bomItem.variant.gst) / 100;
      }
    });

    const generalTotal = subTotal + totalGst;
    return { subTotal, totalGst, generalTotal };
  };

  const totals = calculateTotals();

  return (
    <div className="container py-4" style={{ fontFamily: "inherit", maxWidth: 1200 }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="fw-bold" style={{ fontSize: "2rem" }}>BOM Details</h1>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate('/user/bom')}
        >
          <span className="me-2">&larr;</span>Back to BOM List
        </button>
      </div>

      {/* BOM Information Card */}
      <div className="card p-4 border-0 shadow-sm rounded-4 mb-4">
        <h3 className="fw-semibold mb-3" style={{ fontSize: "1.35rem" }}>BOM Information</h3>
        <div className="row">
          <div className="col-md-3">
            <div className="mb-2">
              <span className="text-muted">BOM Name:</span>
              <div className="fw-semibold">{bomData.name || 'N/A'}</div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-2">
              <span className="text-muted">BOM Code:</span>
              <div className="fw-semibold">{bomData.code || 'N/A'}</div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-2">
              <span className="text-muted">Created Date:</span>
              <div className="fw-semibold">
                {bomData.createdAt ? new Date(bomData.createdAt).toLocaleDateString('en-GB') : 'N/A'}
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-2">
              <span className="text-muted">Last Updated:</span>
              <div className="fw-semibold">
                {bomData.updatedAt ? new Date(bomData.updatedAt).toLocaleDateString('en-GB') : 'N/A'}
              </div>
            </div>
          </div>
        </div>
        {bomData.note && (
          <div className="mt-3">
            <span className="text-muted">Notes:</span>
            <div className="fw-semibold">{bomData.note}</div>
          </div>
        )}
      </div>

      {/* BOM Items Table */}
      <div className="card p-4 border-0 shadow-sm rounded-4 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="fw-semibold mb-0" style={{ fontSize: "1.35rem" }}>BOM Items</h3>
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate(`/user/bom/edit/${id}`)}
            >
              <i className="fas fa-edit me-1"></i> Edit BOM
            </button>
          </div>
        </div>
        
        <div className="d-flex justify-content-end mb-3">
          <div className="input-group" style={{ maxWidth: 280 }}>
            <span className="input-group-text bg-white border-end-0">
              <i className="fas fa-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Search items..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ boxShadow: "none" }}
            />
          </div>
        </div>

        <CommonTable
          columns={bomItemColumns}
          data={tableData}
          itemsPerPageOptions={[10, 15, 25]}
          defaultItemsPerPage={15}
          bordered
        />

        {/* Totals Section */}
        <div className="d-flex flex-column align-items-end mt-4 fs-5">
          <div className="mb-2">
            Sub Total: <span className="fw-semibold">₹{totals.subTotal.toFixed(2)}</span>
          </div>
          <div className="mb-2">
            GST Total: <span className="fw-semibold">₹{totals.totalGst.toFixed(2)}</span>
          </div>
          <div className="fw-bold fs-4 text-primary border-top pt-2">
            General Total: <span>₹{totals.generalTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}